import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { useAuth } from "@/hooks/useAuth";

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { session, isAuthorized, loading, signOut } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-primary">
        <LoadingIndicator size="lg" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  // Signed in, but the email is outside the allow-list (Cloudflare Access is the
  // real gate; this is defence-in-depth).
  if (!isAuthorized) {
    return (
      <div className="grid min-h-screen place-items-center bg-primary px-6">
        <div className="flex max-w-sm flex-col items-center gap-4 text-center">
          <h1 className="text-lg font-semibold text-primary">{t("auth.notAuthorizedTitle")}</h1>
          <p className="text-sm text-tertiary">{t("auth.notAuthorizedBody")}</p>
          <Button color="secondary" size="md" onPress={() => signOut()}>
            {t("shell.signOut")}
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
